import { TimeInput24 } from "./DateTime24";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Siren, Power, Calendar } from "lucide-react";
import { toast } from "sonner";
import { getOncallStatus, setOncallConfig } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useAuth } from "../lib/auth";

export default function OnCallCard() {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startTime, setStartTime] = useState("17:00");
  const [endTime, setEndTime] = useState("08:00");
  const [weekends, setWeekends] = useState(true);
  const [holidays, setHolidays] = useState(true);

  const refresh = async () => {
    try {
      const s = await getOncallStatus();
      setStatus(s);
      const sh = String(s.config.weekday_start_hour).padStart(2, "0");
      const sm = String(s.config.weekday_start_minute ?? 0).padStart(2, "0");
      const eh = String(s.config.weekday_end_hour).padStart(2, "0");
      const em = String(s.config.weekday_end_minute ?? 0).padStart(2, "0");
      setStartTime(`${sh}:${sm}`);
      setEndTime(`${eh}:${em}`);
      setWeekends(s.config.weekends_oncall);
      setHolidays(s.config.holidays_oncall ?? true);
    } catch {
      toast.error("Failed to load on-call status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      const next = await setOncallConfig({
        manual_override: status?.config.manual_override ?? null,
        weekday_start_hour: sh,
        weekday_start_minute: sm,
        weekday_end_hour: eh,
        weekday_end_minute: em,
        weekends_oncall: weekends,
        holidays_oncall: holidays,
        ...patch,
      });
      setStatus(next);
      window.dispatchEvent(new CustomEvent("oncall:changed", { detail: next }));
      toast.success("On-call settings saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const forceOn = () => save({ manual_override: true });
  const forceOff = () => save({ manual_override: false });
  const followSchedule = () => save({ manual_override: null });

  if (loading) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6 text-sm text-slate-500">
        Loading on-call status…
      </section>
    );
  }

  const isOn = status?.is_on_call;
  const reason = status?.reason;

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden"
      data-testid="oncall-card"
    >
      <div className={`px-6 py-4 border-b flex items-center justify-between text-white ${
        isOn
          ? "bg-gradient-to-r from-amber-700 to-amber-600 border-amber-700"
          : "bg-gradient-to-r from-slate-800 to-slate-700 border-slate-700"
      }`}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/15 flex items-center justify-center">
            <Siren className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">After-Hours / On-Call</div>
            <div className="text-xs text-slate-100/80">
              Drivers can self-claim pending jobs while this is ON.
            </div>
          </div>
        </div>
        <div
          className={`px-3 py-1.5 rounded text-xs font-black uppercase tracking-wider ${
            isOn ? "bg-amber-100 text-amber-900" : "bg-slate-200 text-slate-700"
          }`}
          data-testid="oncall-status-badge"
        >
          {isOn ? "ON-CALL · ACTIVE" : "OFF"}{" "}
          <span className="font-semibold opacity-70 normal-case">· {reason}</span>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Manual override */}
        <div>
          <Label className="uppercase-label">Manual override</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              variant={status?.config.manual_override === true ? "default" : "outline"}
              className={status?.config.manual_override === true ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" : ""}
              onClick={forceOn}
              disabled={!isStaff || saving}
              data-testid="oncall-force-on-btn"
            >
              <Power className="h-4 w-4.5" /> Force ON
            </Button>
            <Button
              variant={status?.config.manual_override === false ? "default" : "outline"}
              className={status?.config.manual_override === false ? "bg-slate-700 hover:bg-slate-800" : ""}
              onClick={forceOff}
              disabled={!isStaff || saving}
              data-testid="oncall-force-off-btn"
            >
              <Power className="h-4 w-4.5" /> Force OFF
            </Button>
            <Button
              variant={status?.config.manual_override == null ? "default" : "outline"}
              className={status?.config.manual_override == null ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" : ""}
              onClick={followSchedule}
              disabled={!isStaff || saving}
              data-testid="oncall-auto-btn"
            >
              <Calendar className="h-4 w-4.5" /> Follow Schedule
            </Button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Force ON/OFF overrides the schedule until you reset to <strong>Follow Schedule</strong>.
          </p>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="uppercase-label">Weekday on-call starts at</Label>
            <TimeInput24
              value={startTime}
              onChange={setStartTime}
              disabled={!isStaff}
              dataTestId="oncall-start-time"
            />
            <p className="text-[11px] text-slate-400 mt-1">24h, NZ time · 15-min steps. Default 16:00 (4 pm).</p>
          </div>
          <div>
            <Label className="uppercase-label">Weekday on-call ends at</Label>
            <TimeInput24
              value={endTime}
              onChange={setEndTime}
              disabled={!isStaff}
              dataTestId="oncall-end-time"
            />
            <p className="text-[11px] text-slate-400 mt-1">24h, NZ time · 15-min steps. Default 08:00 (8 am next day).</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={weekends}
            onCheckedChange={setWeekends}
            disabled={!isStaff}
            data-testid="oncall-weekends-toggle"
          />
          <Label className="font-semibold cursor-pointer text-sm" onClick={() => isStaff && setWeekends((v) => !v)}>
            On-call all day Saturday &amp; Sunday
          </Label>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={holidays}
            onCheckedChange={setHolidays}
            disabled={!isStaff}
            data-testid="oncall-holidays-toggle"
          />
          <Label className="font-semibold cursor-pointer text-sm" onClick={() => isStaff && setHolidays((v) => !v)}>
            On-call all day on NZ public holidays
          </Label>
        </div>
        <p className="text-[11px] text-slate-400 -mt-3 ml-12">
          Uses the same Holidays Act mondayisation logic as impound pricing (New Year, Waitangi, ANZAC, Easter, King&apos;s Birthday, Matariki, Labour Day, Christmas, Boxing Day).
        </p>

        <div className="pt-1 flex justify-end">
          <Button
            onClick={async () => { if (await save({})) navigate("/"); }}
            disabled={!isStaff || saving}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="oncall-save-btn"
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </section>
  );
}
